<template lang="pug">
  yz-dialog(v-model="showDialog" persistent scrollable max-width="950")
    yz-card
      yz-dialog-normal-header(:title="isEdit ? $t('panel.edit_payment') : $t('panel.add_payment')" @close="closeDialog")

      yz-dialog-normal-content.pa-6
        v-form(ref="form")
          v-row
            // Left Column - Claim info and Payment method
            v-col(cols="7")
              // Amount due card
              yz-card.pa-3.mb-4(outlined, color="yz-neutral-200")
                .d-flex.justify-space-between.align-start
                  .claim-details
                    .yz-heading-8.text-neutral-700.mb-1 {{ panelName }}
                    .yz-medium-body-xs.text-neutral-500(v-if="claimNumber") {{ $t('panel.claim') }} {{ '#' + claimNumber }}
                  .claim-amounts.text-right
                    .d-flex.gap-5
                      .amount-section
                        .yz-regular-body-xs.text-neutral-500.mb-1 {{ $t('panel.approved_amount') }}
                        .yz-medium-body-xs.text-neutral-700 {{ approvedAmountRaw | displayPrice($currency) }}
                      .amount-section
                        .yz-regular-body-xs.text-neutral-500.mb-1 {{ $t('panel.amount_due') }}
                        .yz-medium-body-xs.text-red-500 {{ amountDueRaw | displayPrice($currency) }}

              // Payment method selection
              .payment-method-section
                .yz-medium-body-sm.text-neutral-700.mb-3 {{ $t('panel.payment_method') }}
                yz-radio-group(v-model="method" direction="horizontal" required :required-error-message="$t('panel.payment_method_required')")
                  yz-radio(value="fpx" :label="$t('panel.online_banking')")
                  yz-radio(value="cheque" :label="$t('panel.cheque')")

              // Payment details card (shown when payment method is selected)
              yz-card.pa-3.mt-5(v-if="method" outlined, color="yz-neutral-200")
                .d-flex.gap-3.mb-5
                  // Amount field
                  .flex-grow-1
                    .yz-medium-body-sm.text-neutral-700.mb-1 {{ $t('panel.amount') }}
                    yz-text-field(v-model.number="amount" :placeholder="$t('panel.enter_amount')" :prefix="$currency.symbol" required :required-error-message="$t('panel.amount_required')" type="number" :rules="[rules.amount]")

                  // Payment reference field
                  .flex-grow-1
                    .yz-medium-body-sm.text-neutral-700.mb-1 {{ $t('panel.payment_reference_no') }}
                    yz-text-field(v-model="refNo" :placeholder="$t('panel.payment_ref_placeholder')")

                // Payment type selection
                .payment-type-section.mb-5
                  .yz-medium-body-sm.text-neutral-700.mb-3 {{ $t('panel.payment_type') }}
                  yz-radio-group(v-model="type" direction="horizontal" required :required-error-message="$t('panel.payment_type_required')")
                    yz-radio(value="full" :label="$t('panel.full_payment')")
                    yz-radio(value="partial" :label="$t('panel.partial_payment')")

                // Payment notes
                .payment-notes-section
                  .yz-medium-body-sm.text-neutral-700.mb-1 {{ $t('panel.payment_notes') }}
                  yz-textarea(v-model="notes" :placeholder="$t('panel.write_payment_note_here')" rows="4")

            // Divider
            yz-divider.mx-4(vertical)

            // Right Column - Upload photo
            v-col
              .yz-medium-body-sm.text-neutral-700.mb-4 {{ $t('panel.upload_photo') }}

              // Upload card
              yz-upload-card(v-model="uploadedPhotos" multiple, append-files, accept="image/*" :primary-title="$t('panel.click_to_upload')" :secondary-title="$t('panel.or_drag_and_drop')" :description="$t('panel.jpg_png_max_5mb')" upload-mode="immediate" @upload-batch-complete="onUploadBatchCompleted")

              // QR Code section
              .d-flex.align-start.gap-4.mt-4
                yz-upload-qr-databin(:qr-size="75" @databin-created="onQrCreated" @error="onQrError")
                .flex-grow-1
                  .yz-medium-body-xs.text-neutral-700.mb-1 {{ $t('panel.add_from_other_devices') }}
                  .yz-regular-body-xs.text-neutral-700 {{ $t('panel.scan_qr_instruction') }}

              // Databin synchronization component (hidden)
              yz-databin-sync(v-if="databinKey" v-model="allSyncedFiles" :added-items.sync="uploadedPhotos" :databin-key="databinKey" @error="onDatabinError" ref="databinSync")

              // Preview files
              .mt-4
                yz-file-preview(v-model="originalFiles" :to-delete-items.sync="originalFileToDelete" :truncate-length="10")
                yz-file-preview(v-model="allSyncedFiles" :to-delete-items.sync="uploadedFileToDelete" :truncate-length="10" @deleted="onDeletedUploadedFiles")

      yz-dialog-normal-actions(:save-label="$t('save')" :cancel-label="$t('cancel')" :loading="loading" @save="onSave" @cancel="closeDialog")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    parentClaim: {
      type: Object,
      required: true
    },
    parentPayment: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      method: null,
      // Databin key for QRcode upload
      databinKey: null,
      originalFiles: [],
      allSyncedFiles: [],
      uploadedPhotos: [],
      uploadedFileToDelete: [],
      originalFileToDelete: [],
      amount: '',
      refNo: '',
      type: null,
      notes: '',
      loading: false,
      rules: {
        amount: value => value >= 0 || this.$t('panel.amount_greater_than_zero')
      }
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    },
    panelName() {
      return this.parentClaim?.panel?.name || null
    },
    claimNumber() {
      return this.parentClaim?.rid || null
    },
    approvedAmountRaw() {
      return this.parentClaim?.approved_amount || 0
    },
    amountDueRaw() {
      const approved = this.parentClaim?.approved_amount || 0
      const received = this.parentClaim?.received_amount || 0
      const adminFee = this.parentClaim?.admin_fee || 0
      return approved - received - adminFee
    },
    isEdit() {
      return !!this.parentPayment
    }
  },
  mounted() {
    this.setData()
  },
  methods: {
    ...mapActions(['showMessage']),
    setData() {
      if (this.parentPayment) {
        // Populate payment method
        this.method = this.parentPayment.method || null

        // Populate payment amount (convert from cents to dollars)
        this.amount = this.parentPayment.amount ? Number(this.parentPayment.amount) / this.$currency.multiplier : null

        // Populate reference number
        this.refNo = this.parentPayment.ref_no || ''

        // Populate payment type
        this.type = this.parentPayment.type || null

        // Populate payment notes
        this.notes = this.parentPayment.notes || ''

        // Populate original files
        this.originalFiles = this.parentPayment.documents || []
      }
    },
    closeDialog() {
      // Clean up synced files and files marked for deletion
      const filesToDelete = []

      // Add all synced files
      if (this.allSyncedFiles.length > 0) {
        const syncedIds = this.allSyncedFiles.map(file => file.hid).filter(Boolean)
        filesToDelete.push(...syncedIds)
      }

      // Add files marked for deletion
      if (this.uploadedFileToDelete.length > 0) {
        const deleteIds = this.uploadedFileToDelete.map(file => file.hid).filter(Boolean)
        filesToDelete.push(...deleteIds)
      }

      // Delete all files if any exist
      if (filesToDelete.length > 0) {
        // Fire and forget - delete files asynchronously without blocking dialog close
        this.$axios.post('/documents/bulk_delete', { documents: filesToDelete }).catch(error => {
          console.error('Error deleting files:', error)
        })
      }

      this.showDialog = false
    },
    async onSave() {
      // Validate form
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', this.$t('panel.fill_required_fields_payment')])
        return
      }

      this.loading = true

      try {
        // Convert amount into cents
        const amountInCents = Math.round(Number(this.amount) * this.$currency.multiplier)

        // Extract document HIDs from both original and newly uploaded files
        const originalFileIds = this.originalFiles.map(file => file.hid).filter(Boolean)
        const newFileIds = this.allSyncedFiles.map(file => file.hid).filter(Boolean)
        const documentIds = [...originalFileIds, ...newFileIds]

        // Build API payload
        const payload = {
          amount: amountInCents,
          ref_no: this.refNo || '',
          method: this.method,
          type: this.type,
          notes: this.notes || '',
          documents: documentIds
        }

        // Determine endpoint and method based on isEdit
        if (this.isEdit) {
          // PATCH - Edit existing payment
          await this.$axios.patch(`/panels/${this.company.slug}/payments/${this.parentPayment.uid}`, payload)
          await this.showMessage(['success', this.$t('panel.payment_updated')])
        } else {
          // POST - Create new payment (add claim field)
          payload.claim = this.parentClaim.id
          await this.$axios.post(`/panels/${this.company.slug}/payments`, payload)
          await this.showMessage(['success', this.$t('panel.payment_added')])
        }

        // Clean up files marked for deletion after successful upload
        const filesToDelete = [...this.originalFileToDelete, ...this.uploadedFileToDelete]

        if (filesToDelete.length > 0) {
          const deleteIds = filesToDelete.map(file => file.hid).filter(Boolean)
          if (deleteIds.length > 0) {
            // Fire and forget - delete files asynchronously
            this.$axios.post('/documents/bulk_delete', { documents: deleteIds }).catch(error => {
              console.error('Error deleting files:', error)
            })
          }
        }

        this.$emit('updated')
        this.showDialog = false
      } catch (error) {
        console.error('Error saving payment:', error)
        await this.showMessage(['error', error.response?.data?.message || this.$t('panel.failed_save_payment')])
      } finally {
        this.loading = false
      }
    },
    onQrCreated(keyData) {
      this.databinKey = keyData.key
    },
    onQrError(error) {
      console.error('QR Code Error:', error)
      this.showMessage(['error', this.$t('panel.qr_code_error')])
    },
    onDatabinError(error) {
      console.error('Databin Sync Error:', error)
      this.showMessage(['error', this.$t('panel.syncing_files_error')])
    },
    onUploadBatchCompleted(uploadResults) {
      // When files are uploaded, add them to the databin sync
      if (this.$refs.databinSync && uploadResults.length > 0) {
        this.$refs.databinSync.addItems(uploadResults)
      }
    },
    onDeletedUploadedFiles(deletedFiles) {
      // When files are deleted from preview, remove them from databin sync
      if (this.$refs.databinSync && deletedFiles.length > 0) {
        this.$refs.databinSync.removeItems(deletedFiles)
      }
    }
  }
}
</script>

<style scoped>
.claim-details {
  max-width: 105px;
}

.claim-amounts {
  text-align: right;
}

.amount-section {
  min-width: 100px;
}

.payment-method-section {
  margin-top: 8px;
}

.payment-details-card {
  background-color: #f9fafb;
  border: 0.75px solid #eeeeee;
  border-radius: 10px;
}
</style>
